import { AuthApiError, authRequest } from '../../../core/api/authApiClient';
import { setAccessToken } from '../../../core/api/tokenStorage';
import i18n from '../../../i18n';
import type {
  AuthRepository,
  CheckEmailCommand,
  CheckEmailResponse,
  SignInCommand,
  SignUpCommand,
  SignUpResponse,
} from '../Domain/repository/AuthRepository';
import type { AuthSession, AuthUserProfile } from '../Domain/entity/AuthTypes';

import { formatUserFullName } from '../../../Utils/formatUserName';

const deriveName = (p: AuthUserProfile): string | undefined => {
  const full = formatUserFullName(p.first_name, p.last_name);
  return full || p.email;
};

export class AuthRepositoryImpl implements AuthRepository {
  // POST /signin: 모금자 로그인
  async signIn(command: SignInCommand): Promise<AuthSession> {
    const res = await authRequest<any>('/signin', {
      body: {
        user_email: command.user_email,
        password: command.password,
        partner: command.partner,
        device_id: command.device_id,
        mobile_os_type: command.mobile_os_type,
        authentication_by: command.authentication_by,
        access_at: command.access_at,
        user_agent: command.user_agent,
      },
    });

    // result_Code 또는 statusCode 확인 (0 또는 200이 아니면 실패)
    if (res.result_Code !== undefined && res.result_Code !== 200 && res.result_Code !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : res.result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignInFailed');
      throw new Error(msg);
    }
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : res.result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignInFailed');
      throw new Error(msg);
    }

    const profile = res as AuthUserProfile;

    const accessToken = String(res?.token?.accessToken ?? '');
    setAccessToken(accessToken);

    return {
      accessToken,
      issuedAt: profile.updated_at ?? profile.created_at,
      user: {
        ...profile,
        id: profile.user_id,
        name: deriveName(profile),
      },
    };
  }

  // POST /users: 회원가입(사용자 생성)
  async signUp(command: SignUpCommand): Promise<SignUpResponse> {
    const res = await authRequest<SignUpResponse>('/users', { body: command });
    
    // result_Code 또는 statusCode 확인 (0 또는 200이 아니면 실패)
    if (res.result_Code !== undefined && res.result_Code !== 200 && res.result_Code !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignUpFailed');
      throw new Error(msg);
    }
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignUpFailed');
      throw new Error(msg);
    }

    return res;
  }

  // POST /users/checkemail: 이메일 중복 확인
  async checkEmail(command: CheckEmailCommand): Promise<CheckEmailResponse> {
    try {
      const res = await authRequest<Omit<CheckEmailResponse, 'available'>>('/users/checkemail', {
        body: { email: command.email },
      });

      const resultCode = res.result_Code;
      const statusCode = res.statusCode;
      const isSuccessResultCode = resultCode === undefined || resultCode === 0 || resultCode === 200;
      const isSuccessStatusCode = statusCode === undefined || statusCode === 0 || statusCode === 200;

      // API가 HTTP 200으로 내려도 payload 코드가 400이면 중복으로 처리
      if (resultCode === 400 || statusCode === 400) {
        return {
          ...res,
          available: false,
        };
      }

      if (!isSuccessResultCode || !isSuccessStatusCode) {
        const msg =
          typeof res.result_message === 'string'
            ? res.result_message
            : i18n.t('common.repositoryErrors.checkEmailFailed', { code: resultCode ?? statusCode ?? 'unknown' });
        throw new Error(msg);
      }

      return {
        ...res,
        available: true,
      };
    } catch (error) {
      if (error instanceof AuthApiError && error.status === 400) {
        const errorBody = (error.body ?? {}) as {
          statusCode?: number;
          result_Code?: number;
          result_message?: string;
          [key: string]: unknown;
        };

        return {
          ...errorBody,
          available: false,
        };
      }

      throw error;
    }
  }
}

